/**
 * Points d'attention calculés automatiquement.
 *
 * Chaque alerte porte une ICÔNE et un LIBELLÉ en plus de sa couleur : un
 * daltonien, une impression noir et blanc ou un mode contrastes forcés doivent
 * rester lisibles. La couleur ne fait que redoubler l'information.
 *
 * Les teintes sont celles de `STATUS_COLORS`, relevées par rapport à la charte
 * PDF pour tenir le contraste sur fond sombre (voir `theme.ts`).
 *
 * Les seuils sont des repères de métier, pas des verdicts — le texte le dit.
 */

import { STATUS_COLORS } from '../lib/theme';
import type { ProductAlert } from '../types';
import { Card } from './Card';

const ICONS: Record<ProductAlert['level'], string> = {
  good: '✓',
  warning: '⚠',
  critical: '✕',
};

const LEVEL_LABELS: Record<ProductAlert['level'], string> = {
  good: 'Bon signal',
  warning: 'À surveiller',
  critical: 'Critique',
};

export function AlertList({ alerts }: { alerts: readonly ProductAlert[] }) {
  return (
    <Card
      title="Points d'attention"
      subtitle="Seuils issus du rapport d'analyse — des repères, pas des verdicts"
      className="chart-grid__wide"
    >
      {alerts.length === 0 ? (
        <p className="alert-empty">
          Aucun signal d'alerte automatique sur cette période.
        </p>
      ) : (
        <ul className="alert-list">
          {alerts.map((alert, index) => (
            <li className="alert" key={index} style={{ borderLeftColor: STATUS_COLORS[alert.level] }}>
              <span className="alert__icon" style={{ color: STATUS_COLORS[alert.level] }} aria-hidden="true">
                {ICONS[alert.level]}
              </span>
              <span className="alert__body">
                <span className="alert__level" style={{ color: STATUS_COLORS[alert.level] }}>
                  {LEVEL_LABELS[alert.level]}
                </span>
                {' — '}
                {alert.message}
              </span>
              <span className="alert__value">{alert.value}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
